import 'ol/ol.css';
import Map from 'ol/Map';
import OSM from 'ol/source/OSM';
import TileLayer from 'ol/layer/Tile';
import View from 'ol/View';
import WMTS, {optionsFromCapabilities} from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import proj4 from 'proj4';
import {register} from 'ol/proj/proj4';
import {get as getProjection} from 'ol/proj';
import {transformExtent} from 'ol/proj';

const params = new URLSearchParams(window.location.search);
const req = params.get('req') || window.location.pathname.split('/').filter(Boolean)[0] || 'ortoruudud';

// `+axis=neu` matches the WMTS service's TopLeftCorner axis order (northing, easting)
// for EPSG:3301, so OpenLayers correctly swaps origin/extent coordinates when building
// the WMTS tile grid from GetCapabilities. This does not affect the coordinate order
// used elsewhere (transformExtent, view center, etc. remain [x, y] as usual).
proj4.defs('EPSG:3301', '+proj=lcc +lat_1=59.33333333333334 +lat_2=58 +lat_0=57.51755393055556 +lon_0=24 +x_0=500000 +y_0=6375000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +axis=neu +no_defs');
register(proj4);

const projection = getProjection('EPSG:3301');
projection.setExtent([350000, 6370000, 745000, 6620000]);

function showError(message) {
  const container = document.getElementById('map');
  if (!container) {
    return;
  }

  container.innerHTML = '';
  const errorBox = document.createElement('div');
  errorBox.textContent = message;
  errorBox.style.cssText = 'display:flex; align-items:center; justify-content:center; height:100%; padding:1rem; box-sizing:border-box; color:#333; background:#f8f8f8; font-family:Arial,sans-serif;';
  container.appendChild(errorBox);
}

// Matches a trailing date such as "..._2026_07_26" in a layer identifier, without
// hardcoding any specific date. Used to dynamically pick the most recent dated layer.
const DATED_LAYER_SUFFIX = /(\d{4})_(\d{2})_(\d{2})$/;

function isLatestLabel(value) {
  return typeof value === 'string' && value.trim().toLowerCase() === 'viimane';
}

function extractLayerDate(identifier) {
  const match = typeof identifier === 'string' ? identifier.match(DATED_LAYER_SUFFIX) : null;
  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
}

function getPreferredLayer(capabilities) {
  const layers = capabilities?.Contents?.Layer || [];
  if (layers.length === 0) {
    return 'Ortofoto';
  }

  // 1. The service itself flags its current "latest" layer via an Identifier or
  //    Title of "viimane" (Estonian for "latest") - trust that if present, since it
  //    may intentionally point to a small partial update rather than full coverage.
  const latest = layers.find((layer) => isLatestLabel(layer.Identifier) || isLatestLabel(layer.Title));
  if (latest) {
    return latest.Identifier;
  }

  // 2. Otherwise, pick whichever dated layer (e.g. "Ortofoto_2026_07_26") is newest,
  //    without hardcoding any specific date so newly published layers are picked up
  //    automatically.
  const newestDated = layers
    .map((layer) => ({layer, date: extractLayerDate(layer.Identifier)}))
    .filter((entry) => entry.date !== null)
    .sort((a, b) => b.date - a.date)[0];
  if (newestDated) {
    return newestDated.layer.Identifier;
  }

  // 3. Fall back to a generic "Ortofoto" layer, or simply the first available layer.
  const generic = layers.find((layer) => layer.Identifier === 'Ortofoto');
  return generic ? generic.Identifier : layers[0].Identifier;
}

function getPreferredFormat(layer) {
  const formats = layer?.Format || [];
  const preferredFormats = ['image/webp', 'image/png', 'image/jpeg'];

  for (const format of preferredFormats) {
    if (formats.includes(format)) {
      return format;
    }
  }

  return formats[0] || 'image/png';
}

function getLayerExtent(layerInfo) {
  if (!layerInfo || !Array.isArray(layerInfo.WGS84BoundingBox)) {
    return null;
  }

  // WMTSCapabilities only exposes the layer's bounding box as WGS84BoundingBox
  // (EPSG:4326). Transform it into the map projection so the view can fit to it.
  return transformExtent(layerInfo.WGS84BoundingBox, 'EPSG:4326', projection.getCode());
}

function fitViewToExtent(mapObject, extent) {
  if (!extent) {
    return;
  }

  mapObject.getView().fit(extent, {
    size: mapObject.getSize(),
    padding: [20, 20, 20, 20],
  });
}

async function initMap() {
  const capabilitiesUrl = `https://wmts.3di.ee/${req}/service?REQUEST=GetCapabilities&SERVICE=WMTS`;

  try {
    const response = await fetch(capabilitiesUrl);
    if (!response.ok) {
      throw new Error(`Capabilities request failed with status ${response.status}`);
    }

    const xmlText = await response.text();
    const capabilities = new WMTSCapabilities().read(xmlText);
    const layerName = getPreferredLayer(capabilities);
    const layerInfo = (capabilities?.Contents?.Layer || []).find((layer) => layer.Identifier === layerName);

    if (!layerInfo) {
      throw new Error('No WMTS layers were found in the capabilities document.');
    }

    const wmtsOptions = optionsFromCapabilities(capabilities, {
      layer: layerName,
      projection: projection,
      format: getPreferredFormat(layerInfo),
    });

    const map = new Map({
      layers: [
        new TileLayer({
          source: new OSM(),
          opacity: 0.7,
        }),
        new TileLayer({
          opacity: 1,
          source: new WMTS({
            ...wmtsOptions,
            attributions:
              '© Ortofotod: <a href="https://3di.ee"' +
              ' target="_blank">3Di</a>',
            style: 'default',
            wrapX: false,
          }),
        }),
      ],
      target: 'map',
      view: new View({
        projection: projection,
        extent: projection.getExtent(),
        center: [555000, 6510000],
        zoom: 3,
      }),
    });

    const layerExtent = getLayerExtent(layerInfo);
    console.log('[WMTS] layer', layerName, 'extent (map projection)', layerExtent);
    fitViewToExtent(map, layerExtent);

    const wmtsSource = map.getLayers().item(1).getSource();
    wmtsSource.on('tileloaderror', (event) => {
      console.warn('[WMTS] tile load error', event.tile?.getTileCoord?.());
    });
  } catch (error) {
    console.error('Unable to initialize WMTS map:', error);
    showError(`Unable to load the WMTS capabilities for ${req}. Please check the service URL.`);
  }
}

initMap();